import React, { useEffect } from "react";
import { useLocation, Link } from "react-router-dom";
import { Check, ArrowUpRight } from "lucide-react";
import { useData } from "@/context/DataContext";
import Seo from "@/components/site/Seo";
import { PageHero } from "@/components/site/Layout";
import { Reveal } from "@/components/site/Reveal";
import { WhatsAppButton } from "@/components/site/WhatsButton";
import ServiceIcon from "@/components/site/ServiceIcon";
import { resolveImg } from "@/lib/api";

export default function Services() {
  const { services, settings } = useData();
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const el = document.getElementById(location.hash.slice(1));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: "smooth", block: "start" }), 300);
    }
  }, [location.hash, services]);

  const schema = {
    "@context": "https://schema.org",
    "@type": "ItemList",
    itemListElement: services.map((s, i) => ({
      "@type": "Service", position: i + 1, name: s.titolo, description: s.breve, provider: { "@type": "HomeAndConstructionBusiness", name: "Animaverde" },
    })),
  };

  return (
    <>
      <Seo title="Servizi" description="Manutenzione giardini, trattamenti fitosanitari, potature, abbattimenti, costruzione e rifacimento giardini, sopralluoghi e preventivi gratuiti in provincia di Varese." path="/servizi" schema={schema} />
      <PageHero
        overline="Cosa facciamo"
        title="Servizi di giardinaggio e cura del verde"
        subtitle="Interventi personalizzati per privati e aziende, valutati caso per caso in base alle caratteristiche dell'area e alle esigenze del cliente."
        breadcrumb={<><Link to="/" className="hover:text-av-lime">Home</Link> / <span className="text-av-white">Servizi</span></>}
      />

      <div className="bg-av-black">
        {services.map((s, i) => (
          <section key={s.id} id={s.slug} className="scroll-mt-24 border-b border-white/10">
            <div className={`mx-auto max-w-[1400px] px-5 sm:px-8 py-16 sm:py-24 grid lg:grid-cols-2 gap-12 items-center ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}>
              <Reveal>
                <div className="relative overflow-hidden rounded-3xl aspect-[4/3]">
                  <img src={resolveImg(s.immagine)} alt={s.titolo} loading="lazy" className="w-full h-full object-cover" />
                </div>
              </Reveal>
              <Reveal delay={0.1}>
                <ServiceIcon name={s.icona} size={30} className="text-av-bright mb-5" strokeWidth={1.5} />
                <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl tracking-tight text-av-white">{s.titolo}</h2>
                <p className="mt-5 text-av-gray leading-relaxed">{s.descrizione}</p>
                {s.per_chi && (
                  <p className="mt-5 text-sm text-av-white/80"><span className="text-av-lime font-semibold">A chi è rivolto: </span>{s.per_chi}</p>
                )}
                {s.esigenze?.length > 0 && (
                  <ul className="mt-6 grid sm:grid-cols-2 gap-2.5">
                    {s.esigenze.map((e) => (
                      <li key={e} className="flex items-start gap-2.5 text-sm text-av-white/85">
                        <Check size={16} className="text-av-lime mt-0.5 shrink-0" strokeWidth={2.5} /> {e}
                      </li>
                    ))}
                  </ul>
                )}
                <div className="mt-8 flex flex-wrap gap-3">
                  <Link to="/contatti" data-testid={`service-cta-${s.slug}`}
                        className="inline-flex items-center gap-2 rounded-full bg-av-bright text-av-black px-6 py-3 text-sm font-semibold hover:bg-av-lime transition-colors">
                    Richiedi un sopralluogo <ArrowUpRight size={15} />
                  </Link>
                  <WhatsAppButton variant="outline" testId={`service-whatsapp-${s.slug}`} />
                </div>
              </Reveal>
            </div>
          </section>
        ))}
      </div>

      {settings && (
        <section className="bg-av-green-dark py-16 text-center" style={{ backgroundColor: "var(--av-green-deep)" }}>
          <div className="mx-auto max-w-2xl px-5">
            <h2 className="font-display text-3xl sm:text-4xl text-av-white">Non trovi quello che cerchi?</h2>
            <p className="mt-4 text-av-gray">Raccontaci di cosa hai bisogno: valutiamo insieme la soluzione più adatta al tuo verde.</p>
            <div className="mt-8 flex flex-wrap justify-center gap-3">
              <WhatsAppButton />
              <a href={`tel:${settings.telefono_link}`} className="inline-flex items-center gap-2 rounded-full border border-white/25 text-av-white px-6 py-3 text-sm font-semibold hover:border-av-lime hover:text-av-lime transition-colors">Chiama il {settings.telefono}</a>
            </div>
          </div>
        </section>
      )}
    </>
  );
}
